import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useCart } from '../contexts/CartContext';
import './Header.css';
import { proxyBucketUrl } from '../utils/imageUtils';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isShopSubmenuOpen, setIsShopSubmenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useUser();
  const { state: cartState, toggleCart, closeCart, updateCartItem, removeFromCart, getTotalItems, getTotalPrice } = useCart();
  const navigate = useNavigate();

  // The logo hangs below the header via absolute positioning, in its own
  // stacking layer: above page content and the header's own background so
  // it stays fully visible normally, but dropped below the burger menu while
  // it's open so the menu fully covers it. We clone the header container's
  // on-screen box here so the logo lands in exactly the same spot.
  const containerRef = useRef<HTMLDivElement>(null);
  const [logoLayerBox, setLogoLayerBox] = useState({ top: 24, height: 42 });

  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setLogoLayerBox({ top: rect.top, height: rect.height });
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsShopSubmenuOpen(false);
  };

  const toggleShopSubmenu = () => {
    setIsShopSubmenuOpen(!isShopSubmenuOpen);
  };

  // Cart functions now handled by useCart hook

  const toggleUserDropdown = () => {
    setIsUserDropdownOpen(!isUserDropdownOpen);
  };

  const closeUserDropdown = () => {
    setIsUserDropdownOpen(false);
  };

  const handleLogout = () => {
    logout();
    closeUserDropdown();
    navigate('/');
  };

  return (
    <>
      <header className="header">
        <div className="container" ref={containerRef}>
          <div className="left-icons">
            <div 
              className={`hamburger-icon ${isMenuOpen ? 'active' : ''}`}
              onClick={toggleMenu}
            >
              <span></span>
              <span></span>
              <span></span>
            </div>
            {isAuthenticated ? (
              <div className="user-profile-dropdown-container">
                <div className="user-profile-icon-container" onClick={toggleUserDropdown}>
                  <img src="/header-icons/person-thin.png" alt="User Account" className="header-icon user-icon" />
                </div>
                
                {isUserDropdownOpen && (
                  <>
                    <div className="profile-dropdown-overlay" onClick={closeUserDropdown}></div>
                    <div className="user-profile-dropdown-menu">
                      <div className="user-profile-info">
                        <h3>{user?.firstName} {user?.lastName}</h3>
                        <p>{user?.email}</p>
                      </div>
                      <hr />
                      <Link to="/account" className="profile-dropdown-item" onClick={closeUserDropdown}>
                        <span className="profile-dropdown-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="12" cy="7" r="4" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </span>
                        My Account
                      </Link>
                      <Link to="/account?tab=orders" className="profile-dropdown-item" onClick={closeUserDropdown}>
                        <span className="profile-dropdown-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M21 8C21 8 21 8 21 8L12 13L3 8" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M3 8V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V8" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </span>
                        My Orders
                      </Link>
                      <Link to="/account?tab=favorites" className="profile-dropdown-item" onClick={closeUserDropdown}>
                        <span className="profile-dropdown-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2L15.09 8.26L22 9L17 13.74L18.18 20.74L12 17.77L5.82 20.74L7 13.74L2 9L8.91 8.26L12 2Z" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </span>
                        Wishlist
                      </Link>
                      <hr />
                      <button className="profile-dropdown-item profile-logout-btn" onClick={handleLogout}>
                        <span className="profile-dropdown-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <polyline points="16,17 21,12 16,7" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <line x1="21" y1="12" x2="9" y2="12" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </span>
                        Logout
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <Link to="/login">
                <img src="/header-icons/person-thin.png" alt="Login" className="header-icon" />
              </Link>
            )}
          </div>

          <div className="right-icons">
            <Link to="/favorites">
              <img src="/header-icons/star.png" alt="Star" className="header-icon" />
            </Link>
            <div className="cart-icon-container" onClick={toggleCart}>
              <img 
                src="/header-icons/shopper.png" 
                alt="Shopper" 
                className="header-icon cart-icon" 
              />
              {getTotalItems() > 0 && (
                <span className="cart-badge">{getTotalItems()}</span>
              )}
            </div>
          </div>
        </div>
      </header>

      <div
        className={`logo-layer ${isMenuOpen ? 'logo-layer--behind-menu' : ''}`}
        style={{ top: logoLayerBox.top, height: logoLayerBox.height }}
      >
        <Link to="/" className="logo">
          <img src="/header-icons/logo.png" alt="EcoFashion Logo" />
        </Link>
      </div>

      <div className={`slide-menu ${isMenuOpen ? 'open' : ''}`}>
        <div className="slide-menu-overlay" onClick={closeMenu}></div>
        <div className="slide-menu-content">
          <nav className="menu-nav">
            <Link to="/about" className="menu-link" onClick={closeMenu}>
              <span>About Us</span>
            </Link>
            <Link to="/space" className="menu-link" onClick={closeMenu}>
              <span>Our Space</span>
            </Link>
            <div className="menu-divider"></div>
            <div className="menu-link-container">
              <div className={`menu-link shop-link ${isShopSubmenuOpen ? 'active' : ''}`} onClick={toggleShopSubmenu}>
                <span>Shop</span>
                <span className={`submenu-arrow ${isShopSubmenuOpen ? 'open' : ''}`}>▼</span>
              </div>
              <div className={`submenu ${isShopSubmenuOpen ? 'open' : ''}`}>
                <Link to="/shop" className="submenu-link" onClick={closeMenu}>
                  <span>All Products</span>
                </Link>
                <Link to="/catalog" className="submenu-link" onClick={closeMenu}>
                  <span>Catalog</span>
                </Link>
                <Link to="/shop?category=accessories" className="submenu-link" onClick={closeMenu}>
                  <span>Accessories</span>
                </Link>
                <Link to="/shop?category=rare" className="submenu-link" onClick={closeMenu}>
                  <span>Rare Finds</span>
                </Link>
                <Link to="/shop?category=stickers" className="submenu-link" onClick={closeMenu}>
                  <span>Stickers</span>
                </Link>
              </div>
            </div>
            <Link to="/outfits" className="menu-link" onClick={closeMenu}>
              <span>Shop the Look</span>
            </Link>
            <div className="menu-divider"></div>
            <Link to="/vintage" className="menu-link" onClick={closeMenu}>
              <span>Why Vintage ?</span>
            </Link>
            <Link to="/policies" className="menu-link" onClick={closeMenu}>
              <span>Info & FAQ</span>
            </Link>
            <div className="menu-divider"></div>
            <Link to="/contact" className="menu-link" onClick={closeMenu}>
              <span>Contact</span>
            </Link>
          </nav>
        </div>
      </div>

      {/* Cart Sidebar */}
      <div className={`header-cart-sidebar ${cartState.isOpen ? 'open' : ''}`}>
        <div className="header-cart-overlay" onClick={closeCart}></div>
        <div className="header-cart-content">
          <div className="header-cart-header">
            <h2 style={{display: 'flex', alignItems: 'center'}}>
              <img 
                src="/header-icons/shopper.png" 
                alt="Cart" 
                style={{width: '32px', height: '32px', marginRight: '12px', filter: 'invert(1)'}}
              />
              Shopping Cart
            </h2>
            <button className="header-cart-close-btn" onClick={closeCart}>×</button>
          </div>
          
          <div className="header-cart-items">
            {cartState.loading ? (
              <div className="header-cart-loading">Loading...</div>
            ) : cartState.items.length === 0 ? (
              <div className="header-cart-empty">
                <p>Your cart is empty</p>
                <Link to="/shop" className="continue-shopping-btn" onClick={closeCart}>
                  Continue Shopping
                </Link>
              </div>
            ) : (
              Array.isArray(cartState.items) ? cartState.items.map(item => (
                <div key={item.id} className="header-cart-item">
                  <Link to={`/product/${item.productId}`} className="header-cart-item-link" onClick={closeCart}>
                    <div className="header-cart-item-image">
                      <img 
                        src={proxyBucketUrl(item.product?.images?.[0]) || '/products/placeholder.svg'} 
                        alt={item.product?.name || 'Product'} 
                      />
                    </div>
                    <div className="header-cart-item-info">
                      <h4>{item.product?.name || 'Unknown Product'}</h4>
                      {item.size && <p className="header-cart-item-size">Size: {item.size}</p>}
                      <div className="header-cart-item-price">€{Number(item.product?.price || 0).toFixed(2)}</div>
                    </div>
                  </Link>
                  <div className="header-cart-item-controls">
                    <button 
                      className="header-remove-item"
                      onClick={() => removeFromCart(item.id)}
                      disabled={cartState.loading}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 6H5H21" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8 6V4C8 3.44772 8.44772 3 9 3H15C15.5523 3 16 3.44772 16 4V6M19 6V20C19 21.1046 18.1046 22 17 22H7C5.89543 22 5 21.1046 5 20V6H19Z" stroke="#900e9b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
              )) : (
                <div className="header-cart-empty">
                  <p>Your cart is empty</p>
                  <Link to="/shop" className="continue-shopping-btn" onClick={closeCart}>
                    Continue Shopping
                  </Link>
                </div>
              )
            )}
          </div>

          {Array.isArray(cartState.items) && cartState.items.length > 0 && (
            <div className="header-cart-footer">
              <div className="header-cart-total">
                <div className="header-subtotal">
                  <span>Subtotal: €{getTotalPrice().toFixed(2)}</span>
                </div>
                <div className="header-shipping">
                  <span>Free Shipping</span>
                </div>
                <div className="header-total">
                  <strong>Total: €{getTotalPrice().toFixed(2)}</strong>
                </div>
              </div>
              <div className="header-cart-actions">
                <Link to="/cart" className="header-view-cart-btn" onClick={closeCart}>
                  View Cart ({getTotalItems()} items)
                </Link>
                <Link to="/checkout" className="header-checkout-btn" onClick={closeCart}>Checkout</Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Header;